import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const statusOptions = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'fallido', label: 'Fallido' },
  { value: 'no_retry', label: 'No retry' },
  { value: 'telefono_invalido', label: 'Teléfono inválido' }
];

const defaultCampaignForm = {
  cliente_id: '',
  encuesta_id: '',
  region_id: '',
  nombre_campania: '',
  mensaje_base: 'Hola, te compartimos tu encuesta de {tienda}: {url}'
};

function onlyDigits(value) {
  return String(value || '').replace(/\D+/g, '');
}

function isPhoneValid(value) {
  return onlyDigits(value).length === 10;
}

function buildWhatsappUrl(phone, message) {
  const digits = onlyDigits(phone);
  const encoded = encodeURIComponent(message || '');
  return `https://wa.me/52${digits}?text=${encoded}`;
}

function buildWhatsappDesktopUrl(phone, message) {
  const digits = onlyDigits(phone);
  const encoded = encodeURIComponent(message || '');
  return `whatsapp://send?phone=52${digits}&text=${encoded}`;
}

function openWhatsappDesktopWithFallback(phone, message) {
  const desktopUrl = buildWhatsappDesktopUrl(phone, message);
  const webUrl = buildWhatsappUrl(phone, message);
  let shouldFallbackToWeb = true;

  const handleBlur = () => {
    shouldFallbackToWeb = false;
    window.removeEventListener('blur', handleBlur);
  };

  window.addEventListener('blur', handleBlur, { once: true });
  window.location.href = desktopUrl;

  setTimeout(() => {
    window.removeEventListener('blur', handleBlur);
    if (shouldFallbackToWeb) {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }
  }, 900);
}

function AdminWhatsappDashboard() {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [syncingCampaign, setSyncingCampaign] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showStatusReasonModal, setShowStatusReasonModal] = useState(false);
  const [statusReasonSubmitting, setStatusReasonSubmitting] = useState(false);
  const [showReactivateSubscriptionModal, setShowReactivateSubscriptionModal] = useState(false);
  const [reactivateSubscriptionSubmitting, setReactivateSubscriptionSubmitting] = useState(false);
  const [pendingReactivateRowId, setPendingReactivateRowId] = useState(0);
  const [pendingStatusRowId, setPendingStatusRowId] = useState(0);
  const [pendingStatusValue, setPendingStatusValue] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [campaignForm, setCampaignForm] = useState(defaultCampaignForm);
  const [dashboard, setDashboard] = useState({ list: [], detail: null });
  const [selectedEnvioId, setSelectedEnvioId] = useState('');
  const [selectedClientFilter, setSelectedClientFilter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('pendiente');
  const [selectedStoreName, setSelectedStoreName] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [detailPage, setDetailPage] = useState(1);
  const [detailLimit, setDetailLimit] = useState(100);
  const [autoMarkSentOnOpen, setAutoMarkSentOnOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const toastOptions = {
    containerId: 'whatsapp-admin-toast',
    position: 'top-center',
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: 'colored',
  };

  const sectionStyle = {
    border: '1px solid #d6dbe1',
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
    background: '#fff'
  };

  useEffect(() => {
    const loadClients = async () => {
      setLoadingClients(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_URL}admin/get_clients.php`);
        setClients(Array.isArray(response.data?.clients) ? response.data.clients : []);
      } catch (e) {
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    loadClients();
  }, []);

  const fetchDashboard = async ({ envioId = '', status = '' } = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (selectedClientFilter) {
        params.append('cliente_id', selectedClientFilter);
      }
      if (envioId) {
        params.append('envio_id', envioId);
      }
      if (status) {
        params.append('status', status);
      }
      params.append('limit', '300');

      const response = await axios.get(`${process.env.REACT_APP_URL}admin/get_whatsapp_envios_dashboard.php?${params.toString()}`);
      if (response.data?.success) {
        const list = response.data.list || [];
        setDashboard({ list, detail: response.data.detail || null });
        if (response.data.detail?.envio_id) {
          const envioIdDetected = String(response.data.detail.envio_id);
          setSelectedEnvioId(envioIdDetected);
          syncCampaignFormFromList(envioIdDetected, list);
        }
      } else {
        setDashboard({ list: [], detail: null });
        const msg = response.data?.message || 'No se pudo cargar dashboard WhatsApp';
        setError(msg);
        toast.error(msg, toastOptions);
      }
    } catch (e) {
      setDashboard({ list: [], detail: null });
      const msg = 'Error al cargar dashboard WhatsApp';
      setError(msg);
      toast.error(msg, toastOptions);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [selectedClientFilter]);

  const fetchDetail = async (envioId, status = selectedStatus, page = detailPage) => {
    if (!envioId) {
      return;
    }

    setLoadingDetail(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (selectedClientFilter) {
        params.append('cliente_id', selectedClientFilter);
      }
      params.append('envio_id', String(envioId));
      if (status) {
        params.append('status', status);
      }
      if (selectedStoreName) {
        params.append('store_name', selectedStoreName);
      }
      if (phoneSearch) {
        params.append('telefono', phoneSearch);
      }
      params.append('page', String(page));
      params.append('limit', String(detailLimit));

      const response = await axios.get(`${process.env.REACT_APP_URL}admin/get_whatsapp_envios_dashboard.php?${params.toString()}`);
      if (response.data?.success) {
        const list = response.data.list || [];
        setDashboard({ list, detail: response.data.detail || null });
        setSelectedEnvioId(String(envioId));
        syncCampaignFormFromList(envioId, list);
      } else {
        const msg = response.data?.message || 'No se pudo cargar detalle de campaña';
        setError(msg);
        toast.error(msg, toastOptions);
      }
    } catch (e) {
      const msg = 'Error al cargar detalle de campaña';
      setError(msg);
      toast.error(msg, toastOptions);
    } finally {
      setLoadingDetail(false);
    }
  };

  const filteredRows = useMemo(() => dashboard.detail?.rows || [], [dashboard.detail]);
  const pagination = useMemo(
    () => dashboard.detail?.pagination || { page: 1, total_pages: 1, total_rows: 0, has_prev: false, has_next: false },
    [dashboard.detail]
  );

  const metrics = useMemo(() => {
    const selectedCampaign = (dashboard.list || []).find((item) => String(item.id) === String(selectedEnvioId));
    if (!selectedCampaign) {
      return { total: 0, sent: 0, pending: 0, failed: 0, invalid: 0, progress: '0.00' };
    }

    const total = Number(selectedCampaign.metrics?.total || 0);
    const sent = Number(selectedCampaign.metrics?.sent || 0);
    const pending = Number(selectedCampaign.metrics?.pending || 0);
    const failed = Number(selectedCampaign.metrics?.failed || 0);
    const invalid = Number(selectedCampaign.metrics?.invalid || 0);
    const progress = total > 0 ? ((sent / total) * 100).toFixed(2) : '0.00';

    return { total, sent, pending, failed, invalid, progress };
  }, [dashboard.list, selectedEnvioId]);

  const exportControlUrl = useMemo(() => {
    if (!selectedEnvioId) {
      return '';
    }

    const params = new URLSearchParams();
    params.append('envio_id', String(selectedEnvioId));
    if (selectedClientFilter) {
      params.append('cliente_id', String(selectedClientFilter));
    }
    if (selectedStatus) {
      params.append('status', selectedStatus);
    }
    if (selectedStoreName) {
      params.append('store_name', selectedStoreName);
    }
    if (phoneSearch) {
      params.append('telefono', phoneSearch);
    }

    return `${process.env.REACT_APP_URL}admin/export_whatsapp_control_excel.php?${params.toString()}`;
  }, [selectedEnvioId, selectedClientFilter, selectedStatus, selectedStoreName, phoneSearch]);

  const onChangeCampaign = (field, value) => {
    setCampaignForm((prev) => ({ ...prev, [field]: value }));
  };

  const syncCampaignFormFromList = (envioId, list = []) => {
    if (!envioId) {
      return;
    }

    const selected = (list || []).find((item) => String(item.id) === String(envioId));
    if (!selected) {
      return;
    }

    setCampaignForm((prev) => ({
      ...prev,
      cliente_id: String(selected.cliente_id || ''),
      encuesta_id: String(selected.encuesta_id || ''),
      region_id: String(selected.region_id || ''),
      nombre_campania: selected.nombre_campania || '',
      mensaje_base: selected.mensaje_base || prev.mensaje_base,
    }));
  };

  const onCreateCampaign = async () => {
    setCreatingCampaign(true);
    setError('');
    setMessage('');

    try {
      const payload = new URLSearchParams();
      payload.append('cliente_id', campaignForm.cliente_id || '0');
      payload.append('encuesta_id', campaignForm.encuesta_id || '0');
      payload.append('region_id', campaignForm.region_id || '');
      payload.append('nombre_campania', campaignForm.nombre_campania || '');
      payload.append('mensaje_base', campaignForm.mensaje_base || '');

      const createResp = await axios.post(
        `${process.env.REACT_APP_URL}admin/create_whatsapp_envio_masivo.php`,
        payload,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      if (!createResp.data?.success || !createResp.data?.envio_id) {
        const msg = createResp.data?.message || 'No se pudo crear campaña WhatsApp';
        setError(msg);
        toast.error(msg, toastOptions);
        return;
      }

      const envioId = String(createResp.data.envio_id);
      const seedPayload = new URLSearchParams();
      seedPayload.append('envio_id', envioId);

      const seedResp = await axios.post(
        `${process.env.REACT_APP_URL}admin/seed_whatsapp_control_envios.php`,
        seedPayload,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      if (!seedResp.data?.success) {
        const msg = seedResp.data?.message || 'No se pudo generar cola de destinatarios';
        setError(msg);
        toast.error(msg, toastOptions);
        return;
      }

      const okMsg = `Campaña creada. Registros insertados: ${seedResp.data?.inserted || 0}. Teléfonos inválidos: ${seedResp.data?.invalid_phones || 0}.`;
      setMessage(okMsg);
      toast.success(okMsg, toastOptions);
      setDetailPage(1);
      await fetchDetail(envioId, selectedStatus, 1);
    } catch (e) {
      const msg = 'Error creando campaña WhatsApp';
      setError(msg);
      toast.error(msg, toastOptions);
    } finally {
      setCreatingCampaign(false);
    }
  };

  const onSyncCampaignQueue = async () => {
    if (!selectedEnvioId) {
      const msg = 'Selecciona primero una campaña para sincronizar destinatarios';
      setError(msg);
      toast.warning(msg, toastOptions);
      return;
    }

    setSyncingCampaign(true);
    const syncToastId = toast.loading('Sincronizando nuevos contactos...', toastOptions);
    try {
      const payload = new URLSearchParams();
      payload.append('envio_id', String(selectedEnvioId));

      const response = await axios.post(
        `${process.env.REACT_APP_URL}admin/seed_whatsapp_control_envios.php`,
        payload,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      if (!response.data?.success) {
        const msg = response.data?.message || 'No se pudo sincronizar la cola';
        setError(msg);
        toast.update(syncToastId, {
          render: msg,
          type: 'error',
          isLoading: false,
          autoClose: 4500,
        });
        return;
      }

      const inserted = Number(response.data?.inserted || 0);
      const invalidPhones = Number(response.data?.invalid_phones || 0);
      const okMsg = `Sincronización lista. Nuevos registros: ${inserted}. Teléfonos inválidos: ${invalidPhones}.`;
      setMessage(okMsg);
      toast.update(syncToastId, {
        render: okMsg,
        type: 'success',
        isLoading: false,
        autoClose: 4000,
      });

      await fetchDetail(selectedEnvioId, selectedStatus, detailPage);
    } catch (e) {
      const msg = 'Error sincronizando nuevos contactos';
      setError(msg);
      toast.update(syncToastId, {
        render: msg,
        type: 'error',
        isLoading: false,
        autoClose: 4500,
      });
    } finally {
      setSyncingCampaign(false);
    }
  };

  const onSelectCampaign = (envioId) => {
    const selected = (dashboard.list || []).find((item) => String(item.id) === String(envioId));
    if (selected) {
      setCampaignForm((prev) => ({
        ...prev,
        cliente_id: String(selected.cliente_id || ''),
        encuesta_id: String(selected.encuesta_id || ''),
        region_id: String(selected.region_id || ''),
        nombre_campania: selected.nombre_campania || '',
        mensaje_base: selected.mensaje_base || prev.mensaje_base,
      }));
    }
    setDetailPage(1);
    fetchDetail(envioId, selectedStatus, 1);
  };

  const onSelectCampaignFromEdit = (envioId) => {
    if (!envioId) {
      setSelectedEnvioId('');
      setCampaignForm(defaultCampaignForm);
      return;
    }
    onSelectCampaign(envioId);
  };

  const onSaveCampaignMessage = async () => {
    if (!selectedEnvioId) {
      const msg = 'Selecciona primero una campaña para guardar el mensaje base';
      setError(msg);
      toast.warning(msg, toastOptions);
      return;
    }

    setSavingCampaign(true);
    const saveToastId = toast.loading('Guardando cambios de campaña...', toastOptions);

    try {
      setError('');
      setMessage('');

      const payload = new URLSearchParams();
      payload.append('envio_id', String(selectedEnvioId));
      payload.append('mensaje_base', campaignForm.mensaje_base || '');
      payload.append('nombre_campania', campaignForm.nombre_campania || '');
      payload.append('region_id', campaignForm.region_id || '');

      const response = await axios.post(
        `${process.env.REACT_APP_URL}admin/update_whatsapp_envio_masivo.php`,
        payload,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      if (!response.data?.success) {
        const msg = response.data?.message || 'No se pudo guardar mensaje base';
        setError(msg);
        toast.update(saveToastId, {
          render: msg,
          type: 'error',
          isLoading: false,
          autoClose: 4500,
        });
        return;
      }

      const okMsg = 'Mensaje base guardado en la campaña seleccionada';
      setMessage(okMsg);
      toast.update(saveToastId, {
        render: okMsg,
        type: 'success',
        isLoading: false,
        autoClose: 3500,
      });
      await fetchDetail(selectedEnvioId, selectedStatus, detailPage);
    } catch (e) {
      const msg = 'Error guardando mensaje base';
      setError(msg);
      toast.update(saveToastId, {
        render: msg,
        type: 'error',
        isLoading: false,
        autoClose: 4500,
      });
    } finally {
      setSavingCampaign(false);
    }
  };

  const onDeleteCampaign = async () => {
    if (!selectedEnvioId) {
      const msg = 'Selecciona primero una campaña para eliminar';
      setError(msg);
      toast.warning(msg, toastOptions);
      return;
    }

    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!selectedEnvioId) {
      setShowDeleteConfirmModal(false);
      return;
    }

    setDeletingCampaign(true);
    setError('');
    setMessage('');
    try {
      const payload = new URLSearchParams();
      payload.append('envio_id', String(selectedEnvioId));

      const response = await axios.post(
        `${process.env.REACT_APP_URL}admin/delete_whatsapp_envio_masivo.php`,
        payload,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      if (!response.data?.success) {
        const msg = response.data?.message || 'No se pudo eliminar la campaña';
        setError(msg);
        toast.error(msg, toastOptions);
        return;
      }

      const okMsg = 'Campaña eliminada correctamente';
      setMessage(okMsg);
      toast.info(okMsg, toastOptions);
      setShowDeleteConfirmModal(false);
      setSelectedEnvioId('');
      setCampaignForm(defaultCampaignForm);
      await fetchDashboard();
    } catch (e) {
      const msg = 'Error eliminando campaña';
      setError(msg);
      toast.error(msg, toastOptions);
    } finally {
      setDeletingCampaign(false);
    }
  };

  const closeDeleteConfirmModal = () => {
    if (!deletingCampaign) {
      setShowDeleteConfirmModal(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'enviado':
        return 'bg-success';
      case 'pendiente':
        return 'bg-warning text-dark';
      case 'fallido':
        return 'bg-danger';
      case 'no_retry':
        return 'bg-dark';
      case 'telefono_invalido':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'enviado':
        return 'Enviado';
      case 'pendiente':
        return 'Pendiente';
      case 'fallido':
        return 'Fallido';
      case 'no_retry':
        return 'No retry';
      case 'telefono_invalido':
        return 'Teléfono inválido';
      default:
        return status || 'Sin estatus';
    }
  };

  const onChangeStatusFilter = (status) => {
    setSelectedStatus(status);
    setDetailPage(1);
    if (selectedEnvioId) {
      fetchDetail(selectedEnvioId, status, 1);
    }
  };

  const applyAdvancedFilters = () => {
    setDetailPage(1);
    if (selectedEnvioId) {
      fetchDetail(selectedEnvioId, selectedStatus, 1);
    }
  };

  const changePage = (nextPage) => {
    if (!selectedEnvioId) {
      return;
    }

    setDetailPage(nextPage);
    fetchDetail(selectedEnvioId, selectedStatus, nextPage);
  };

  const buildPageRange = (currentPage, totalPages, maxVisible = 10) => {
    const safeCurrent = Math.max(1, Number(currentPage) || 1);
    const safeTotal = Math.max(1, Number(totalPages) || 1);
    const size = Math.max(1, maxVisible);

    let start = Math.max(1, safeCurrent - Math.floor(size / 2));
    let end = Math.min(safeTotal, start + size - 1);

    if ((end - start + 1) < size) {
      start = Math.max(1, end - size + 1);
    }

    const pages = [];
    for (let p = start; p <= end; p += 1) {
      pages.push(p);
    }
    return pages;
  };

  const visiblePages = useMemo(
    () => buildPageRange(pagination.page, pagination.total_pages, 10),
    [pagination.page, pagination.total_pages]
  );

  const renderPagerControls = () => (
    <div style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => changePage(Math.max(1, Number(pagination.page) - 1))}
        disabled={!pagination.has_prev || loadingDetail}
      >
        Anterior
      </button>

      {visiblePages.map((pageNumber) => {
        const isCurrent = Number(pageNumber) === Number(pagination.page);
        return (
          <button
            key={`page-${pageNumber}`}
            className={`btn btn-sm ${isCurrent ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => changePage(pageNumber)}
            disabled={loadingDetail}
            type="button"
          >
            {pageNumber}
          </button>
        );
      })}

      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => changePage(Number(pagination.page) + 1)}
        disabled={!pagination.has_next || loadingDetail}
      >
        Siguiente
      </button>

      <span style={{ marginLeft: 8 }}>
        Página {pagination.page} de {pagination.total_pages} · Registros: {pagination.total_rows}
      </span>
    </div>
  );

  const onChangeLimit = (value) => {
    const parsed = Number(value || 100);
    setDetailLimit(parsed);
    setDetailPage(1);
    if (selectedEnvioId) {
      const params = new URLSearchParams();
      if (selectedClientFilter) {
        params.append('cliente_id', selectedClientFilter);
      }
      params.append('envio_id', String(selectedEnvioId));
      if (selectedStatus) {
        params.append('status', selectedStatus);
      }
      if (selectedStoreName) {
        params.append('store_name', selectedStoreName);
      }
      if (phoneSearch) {
        params.append('telefono', phoneSearch);
      }
      params.append('page', '1');
      params.append('limit', String(parsed));

      setLoadingDetail(true);
      axios
        .get(`${process.env.REACT_APP_URL}admin/get_whatsapp_envios_dashboard.php?${params.toString()}`)
        .then((response) => {
          if (response.data?.success) {
            const list = response.data.list || [];
            setDashboard({ list, detail: response.data.detail || null });
            syncCampaignFormFromList(selectedEnvioId, list);
          }
        })
        .catch(() => {
          const msg = 'Error al cambiar tamaño de página';
          setError(msg);
          toast.error(msg, toastOptions);
        })
        .finally(() => setLoadingDetail(false));
    }
  };

  const updateRowStatus = async (rowId, status, reason = '', reactivateSubscription = false) => {
    try {
      const payload = new URLSearchParams();
      payload.append('id', String(rowId));
      payload.append('estatus', status);
      if (status === 'fallido' || status === 'no_retry') {
        payload.append('error', reason);
      }
      if (reactivateSubscription) {
        payload.append('reactivate_subscription', '1');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_URL}admin/update_whatsapp_control_status.php`,
        payload,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      if (!response.data?.success) {
        const msg = response.data?.message || 'No se pudo actualizar estatus';
        setError(msg);
        toast.error(msg, toastOptions);
        return;
      }

      if (status === 'pendiente' && response.data?.subscription_reactivated) {
        toast.success('Estatus en pendiente y suscripción reactivada', toastOptions);
      } else {
        toast.success(`Estatus actualizado a: ${getStatusLabel(status)}`, toastOptions);
      }
      await fetchDetail(selectedEnvioId, selectedStatus);
    } catch (e) {
      const msg = 'Error actualizando estatus';
      setError(msg);
      toast.error(msg, toastOptions);
    }
  };

  const onToggleRowStatus = async (row, targetStatus) => {
    const currentStatus = String(row?.estatus || '');
    const nextStatus = currentStatus === targetStatus ? 'pendiente' : targetStatus;

    if (nextStatus === 'pendiente' && (currentStatus === 'fallido' || currentStatus === 'no_retry')) {
      setPendingReactivateRowId(Number(row.id || 0));
      setShowReactivateSubscriptionModal(true);
      return;
    }

    if (nextStatus === 'fallido' || nextStatus === 'no_retry') {
      setPendingStatusRowId(Number(row.id || 0));
      setPendingStatusValue(nextStatus);
      setStatusReason('');
      setShowStatusReasonModal(true);
      return;
    }

    await updateRowStatus(row.id, nextStatus);
  };

  const closeReactivateSubscriptionModal = () => {
    if (reactivateSubscriptionSubmitting) {
      return;
    }

    setShowReactivateSubscriptionModal(false);
    setPendingReactivateRowId(0);
  };

  const confirmPendingWithoutReactivation = async () => {
    if (!pendingReactivateRowId) {
      closeReactivateSubscriptionModal();
      return;
    }

    setReactivateSubscriptionSubmitting(true);
    await updateRowStatus(pendingReactivateRowId, 'pendiente', '', false);
    setReactivateSubscriptionSubmitting(false);
    closeReactivateSubscriptionModal();
  };

  const confirmPendingWithReactivation = async () => {
    if (!pendingReactivateRowId) {
      closeReactivateSubscriptionModal();
      return;
    }

    setReactivateSubscriptionSubmitting(true);
    await updateRowStatus(pendingReactivateRowId, 'pendiente', '', true);
    setReactivateSubscriptionSubmitting(false);
    closeReactivateSubscriptionModal();
  };

  const closeStatusReasonModal = () => {
    if (statusReasonSubmitting) {
      return;
    }

    setShowStatusReasonModal(false);
    setPendingStatusRowId(0);
    setPendingStatusValue('');
    setStatusReason('');
  };

  const confirmStatusReasonModal = async () => {
    if (!pendingStatusRowId || !pendingStatusValue) {
      closeStatusReasonModal();
      return;
    }

    setStatusReasonSubmitting(true);
    await updateRowStatus(pendingStatusRowId, pendingStatusValue, statusReason.trim());
    setStatusReasonSubmitting(false);
    closeStatusReasonModal();
  };

  const onCopyMessage = async (msg, valid) => {
    if (!valid) {
      toast.warning('No se puede copiar mensaje: teléfono inválido', toastOptions);
      return;
    }

    try {
      await navigator.clipboard?.writeText(msg);
      toast.info('Mensaje copiado al portapapeles', toastOptions);
    } catch (e) {
      toast.error('No se pudo copiar el mensaje', toastOptions);
    }
  };

  const onOpenWhatsapp = async (row, phone, msg, valid) => {
    if (!valid) {
      toast.warning('Teléfono inválido: corrige el registro antes de abrir WhatsApp', toastOptions);
      return;
    }

    openWhatsappDesktopWithFallback(phone, msg);
    toast.info('Abriendo WhatsApp Desktop (con fallback web)', toastOptions);

    if (autoMarkSentOnOpen) {
      await updateRowStatus(row.id, 'enviado');
    }
  };

  return (
    <div className="adminelement whatsapp-dashboard">
      <h2>Dashboard de envíos WhatsApp</h2>
      <p style={{ marginBottom: 12 }}>
        Esta pestaña es independiente a envíos por correo. Aquí operaremos campañas WhatsApp con URL personalizada.
      </p>

      <div style={{ marginBottom: 12 }}>
        <label htmlFor="client-filter-whatsapp">Cliente: </label>
        <select
          id="client-filter-whatsapp"
          value={selectedClientFilter}
          onChange={(e) => setSelectedClientFilter(e.target.value)}
        >
          <option value="">Todos los clientes</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>{client.nombre}</option>
          ))}
        </select>
      </div>

      <div style={{ ...sectionStyle, borderLeft: '5px solid #198754' }}>
        <h3 style={{ marginTop: 0, color: '#198754' }}>1. Crear campaña</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))', gap: 10 }}>
          <label>
            <div>Cliente</div>
            <select
              value={campaignForm.cliente_id}
              onChange={(e) => onChangeCampaign('cliente_id', e.target.value)}
            >
              <option value="">Seleccionar cliente</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.nombre}</option>
              ))}
            </select>
          </label>

          <label>
            <div>Encuesta ID</div>
            <input
              type="number"
              min="1"
              value={campaignForm.encuesta_id}
              onChange={(e) => onChangeCampaign('encuesta_id', e.target.value)}
            />
          </label>

          <label>
            <div>Región/Tienda (opcional)</div>
            <input
              type="number"
              min="1"
              value={campaignForm.region_id}
              onChange={(e) => onChangeCampaign('region_id', e.target.value)}
            />
          </label>

          <label>
            <div>Nombre de campaña</div>
            <input
              type="text"
              value={campaignForm.nombre_campania}
              onChange={(e) => onChangeCampaign('nombre_campania', e.target.value)}
            />
          </label>
        </div>

        <label style={{ marginTop: 10, width: '100%' }}>
          <div>Mensaje base</div>
          <textarea
            rows={3}
            style={{ width: '100%' }}
            value={campaignForm.mensaje_base}
            onChange={(e) => onChangeCampaign('mensaje_base', e.target.value)}
          />
        </label>
        <small style={{ display: 'block', marginTop: 8, color: '#5c636a' }}>
          Si defines Región/Tienda, la campaña se segmenta a ese alcance. Si lo dejas vacío, aplica sin filtro regional.
        </small>
        <small style={{ display: 'block', marginTop: 4, color: '#5c636a' }}>
          Shortcodes disponibles: {'{url}'}, {'{telefono}'}, {'{tienda}'}.
        </small>

        <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-success" onClick={onCreateCampaign} disabled={loadingClients || creatingCampaign}>
            {creatingCampaign ? 'Creando campaña...' : (loadingClients ? 'Cargando clientes...' : 'Crear campaña + generar cola')}
          </button>
        </div>
      </div>

      <div style={{ ...sectionStyle, borderLeft: '5px solid #0d6efd' }}>
        <h3 style={{ marginTop: 0, color: '#0d6efd' }}>2. Editar campaña seleccionada</h3>
        <label style={{ display: 'block', marginBottom: 8 }}>
          <div>Seleccionar campaña a editar</div>
          <select
            value={selectedEnvioId}
            onChange={(e) => onSelectCampaignFromEdit(e.target.value)}
            style={{ minWidth: 320 }}
          >
            <option value="">Selecciona una campaña</option>
            {(dashboard.list || []).map((item) => (
              <option key={item.id} value={item.id}>
                #{item.id} - {item.nombre_campania || 'Sin nombre'} (Cliente: {item.cliente_nombre || item.cliente_id})
              </option>
            ))}
          </select>
        </label>
        <p style={{ marginBottom: 10 }}>
          Campaña activa: <strong>{selectedEnvioId ? `#${selectedEnvioId}` : 'ninguna seleccionada'}</strong>
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))', gap: 10 }}>
          <label>
            <div>Nombre de campaña</div>
            <input
              type="text"
              value={campaignForm.nombre_campania}
              onChange={(e) => onChangeCampaign('nombre_campania', e.target.value)}
            />
          </label>
          <label>
            <div>Región/Tienda (opcional)</div>
            <input
              type="number"
              min="1"
              value={campaignForm.region_id}
              onChange={(e) => onChangeCampaign('region_id', e.target.value)}
            />
          </label>
        </div>

        <label style={{ marginTop: 10, width: '100%' }}>
          <div>Mensaje base</div>
          <textarea
            rows={3}
            style={{ width: '100%' }}
            value={campaignForm.mensaje_base}
            onChange={(e) => onChangeCampaign('mensaje_base', e.target.value)}
          />
        </label>
        <small style={{ display: 'block', marginTop: 8, color: '#5c636a' }}>
          Este campo sirve para segmentar por región/tienda; no es obligatorio para guardar o ejecutar la campaña.
        </small>
        <small style={{ display: 'block', marginTop: 4, color: '#5c636a' }}>
          Shortcodes disponibles: {'{url}'}, {'{telefono}'}, {'{tienda}'}.
        </small>

        <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={onSaveCampaignMessage} disabled={!selectedEnvioId || savingCampaign}>
            {savingCampaign ? 'Guardando...' : 'Guardar cambios campaña'}
          </button>
          <button className="btn btn-danger" onClick={onDeleteCampaign} disabled={!selectedEnvioId || deletingCampaign}>
            {deletingCampaign ? 'Eliminando...' : 'Eliminar campaña'}
          </button>
        </div>
      </div>

      <div style={{ ...sectionStyle, borderLeft: '5px solid #6f42c1' }}>
        <h3 style={{ marginTop: 0, color: '#6f42c1' }}>3. Operación y seguimiento</h3>
        <p style={{ marginBottom: 0 }}>
          Selecciona una campaña en la tabla para habilitar filtros, acciones y exportación.
        </p>
      </div>

      {message ? <p style={{ color: '#0f5132', marginBottom: 12 }}>{message}</p> : null}

      {error ? <p style={{ color: 'red' }}>{error}</p> : null}
      {loading ? <p>Cargando campañas WhatsApp...</p> : null}

      {dashboard.list.length > 0 ? (
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table className="table table-striped table-sm">
            <thead>
              <tr>
                <th>ID</th>
                <th>Cliente</th>
                <th>Encuesta</th>
                <th>Campaña</th>
                <th>Total</th>
                <th>Enviados</th>
                <th>Pendientes</th>
                <th>Fallidos</th>
                <th>Inválidos</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.list.map((item) => {
                const isSelected = String(selectedEnvioId) === String(item.id);
                return (
                <tr
                  key={item.id}
                  style={isSelected ? { backgroundColor: '#e7f1ff' } : undefined}
                >
                  <td>{item.id}</td>
                  <td>{item.cliente_nombre || item.cliente_id}</td>
                  <td>{item.encuesta_id}</td>
                  <td>{item.nombre_campania}</td>
                  <td>{item.metrics?.total}</td>
                  <td><span className="badge bg-success">{item.metrics?.sent}</span></td>
                  <td><span className="badge bg-warning text-dark">{item.metrics?.pending}</span></td>
                  <td><span className="badge bg-danger">{item.metrics?.failed}</span></td>
                  <td><span className="badge bg-secondary">{item.metrics?.invalid}</span></td>
                  <td>
                    <button
                      className={`btn btn-sm ${isSelected ? 'btn-outline-primary' : 'btn-primary'}`}
                      onClick={() => onSelectCampaign(item.id)}
                      disabled={loadingDetail && String(selectedEnvioId) === String(item.id)}
                    >
                      {loadingDetail && String(selectedEnvioId) === String(item.id)
                        ? 'Cargando...'
                        : (isSelected ? 'Seleccionada' : 'Ver')}
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="card" style={{ padding: 10, minWidth: 160 }}><strong>Total</strong><div>{metrics.total}</div></div>
        <div className="card" style={{ padding: 10, minWidth: 160 }}><strong>Enviados</strong><div>{metrics.sent}</div></div>
        <div className="card" style={{ padding: 10, minWidth: 160 }}><strong>Pendientes</strong><div>{metrics.pending}</div></div>
        <div className="card" style={{ padding: 10, minWidth: 160 }}><strong>Fallidos</strong><div>{metrics.failed}</div></div>
        <div className="card" style={{ padding: 10, minWidth: 160 }}><strong>Teléfono inválido</strong><div>{metrics.invalid}</div></div>
        <div className="card" style={{ padding: 10, minWidth: 160 }}><strong>Avance</strong><div>{metrics.progress}%</div></div>
      </div>

      <div style={{ marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="whatsapp-status-filter">Estatus:</label>
        <select
          id="whatsapp-status-filter"
          value={selectedStatus}
          onChange={(e) => onChangeStatusFilter(e.target.value)}
        >
          <option value="">Todos</option>
          {statusOptions.map((status) => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
        <label htmlFor="whatsapp-store-filter">Tienda:</label>
        <input
          id="whatsapp-store-filter"
          type="text"
          list="whatsapp-store-options"
          value={selectedStoreName}
          onChange={(e) => setSelectedStoreName(e.target.value)}
          placeholder="Buscar tienda"
          style={{ width: 220 }}
        />
        <datalist id="whatsapp-store-options">
          {(dashboard.detail?.store_suggestions || []).map((storeName) => (
            <option key={storeName} value={storeName} />
          ))}
        </datalist>
        <label htmlFor="whatsapp-phone-search">Teléfono:</label>
        <input
          id="whatsapp-phone-search"
          type="text"
          value={phoneSearch}
          onChange={(e) => setPhoneSearch(onlyDigits(e.target.value))}
          placeholder="Buscar teléfono"
          style={{ width: 160 }}
        />
        <button className="btn btn-sm btn-primary" onClick={applyAdvancedFilters} disabled={!selectedEnvioId}>
          Aplicar filtros
        </button>
        <button className="btn btn-sm btn-outline-success" onClick={onSyncCampaignQueue} disabled={!selectedEnvioId || syncingCampaign}>
          {syncingCampaign ? 'Sincronizando...' : 'Sincronizar nuevos contactos'}
        </button>
        <label htmlFor="whatsapp-page-limit">Filas:</label>
        <select
          id="whatsapp-page-limit"
          value={detailLimit}
          onChange={(e) => onChangeLimit(e.target.value)}
        >
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
          <option value={300}>300</option>
        </select>
        <a
          className="btn btn-sm btn-success"
          href={exportControlUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!exportControlUrl) {
              e.preventDefault();
            }
          }}
        >
          Descargar Excel control
        </a>
        <label style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={autoMarkSentOnOpen}
            onChange={(e) => setAutoMarkSentOnOpen(e.target.checked)}
          />
          Auto-marcar enviado al abrir WhatsApp
        </label>
      </div>

      {renderPagerControls()}

      <div style={{ overflowX: 'auto' }}>
        <table className="table table-striped table-sm">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Teléfono</th>
              <th>Válido 10 dígitos</th>
              <th>Región/Tienda</th>
              <th>URL personalizada</th>
              <th>Estatus</th>
              <th>Intentos</th>
              <th>Error</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => {
              const phone = row.telefono_normalizado || row.telefono_raw || '';
              const valid = row.telefono_valido === true || isPhoneValid(phone);
              const msg = row.mensaje_render || `Hola, te compartimos tu encuesta de ${row.region_nombre || 'tu tienda'}: ${row.url_personalizada}`;
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.nombre_contacto || '-'}</td>
                  <td>{phone}</td>
                  <td>
                    <span className={`badge ${valid ? 'bg-success' : 'bg-danger'}`}>
                      {valid ? 'Sí' : 'No'}
                    </span>
                  </td>
                  <td>{row.region_nombre}</td>
                  <td style={{ minWidth: 300 }}>{row.url_personalizada}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(row.estatus)}`}>
                      {getStatusLabel(row.estatus)}
                    </span>
                  </td>
                  <td>{row.intentos}</td>
                  <td>{row.error || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexDirection: 'column', minWidth: 170 }}>
                      <button
                        className="btn btn-whatsapp"
                        type="button"
                        onClick={() => onOpenWhatsapp(row, phone, msg, valid)}
                        disabled={!valid}
                        title="Abre WhatsApp con el mensaje prellenado"
                        style={{
                          fontSize: 14,
                          padding: '0.45rem 0.75rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          width: '100%',
                        }}
                      >
                        <WhatsAppIcon style={{ fontSize: 32 }} />
                        Abrir
                      </button>
                      <button
                        className={`btn btn-sm ${row.estatus === 'enviado' ? 'btn-success' : 'btn-outline-success'}`}
                        onClick={() => onToggleRowStatus(row, 'enviado')}
                        disabled={!valid}
                        title={row.estatus === 'enviado' ? 'Quita marca de enviado (regresa a pendiente)' : 'Marca este contacto como enviado'}
                        style={{ width: '100%', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}
                      >
                        {row.estatus === 'enviado' ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                        Enviado
                      </button>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => onCopyMessage(msg, valid)}
                        disabled={!valid}
                        title="Copia el mensaje al portapapeles"
                        style={{ width: '100%', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}
                      >
                        Copiar mensaje
                      </button>
                      <button
                        className={`btn btn-sm ${row.estatus === 'fallido' ? 'btn-danger' : 'btn-outline-danger'}`}
                        onClick={() => onToggleRowStatus(row, 'fallido')}
                        title={row.estatus === 'fallido' ? 'Quita marca de fallido (regresa a pendiente)' : 'Marca fallido y da de baja al contacto para futuros envíos'}
                        style={{ width: '100%', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}
                      >
                        {row.estatus === 'fallido' ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                        Marcar fallido
                      </button>
                      <button
                        className={`btn btn-sm ${row.estatus === 'no_retry' ? 'btn-warning text-dark' : 'btn-outline-warning text-dark'}`}
                        onClick={() => onToggleRowStatus(row, 'no_retry')}
                        title={row.estatus === 'no_retry' ? 'Quita no retry (regresa a pendiente)' : 'Marca no retry y da de baja al contacto para futuros envíos'}
                        style={{ width: '100%', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}
                      >
                        {row.estatus === 'no_retry' ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                        No retry
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {renderPagerControls()}

      {loadingDetail ? <p>Cargando detalle...</p> : null}

      <Dialog
        open={showDeleteConfirmModal}
        onClose={closeDeleteConfirmModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ backgroundColor: '#fff3cd' }}>
          Confirmar borrado de campaña
        </DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 0, marginTop: 8 }}>
            Esta acción eliminará la campaña <strong>#{selectedEnvioId}</strong> y toda su cola de WhatsApp. Esta operación no se puede deshacer.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmModal} disabled={deletingCampaign} color="warning" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmDeleteCampaign} disabled={deletingCampaign} color="error" variant="contained">
            {deletingCampaign ? 'Eliminando...' : 'Sí, eliminar campaña'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showStatusReasonModal}
        onClose={closeStatusReasonModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ backgroundColor: '#fff3cd' }}>
          {pendingStatusValue === 'fallido' ? 'Motivo de fallo (opcional)' : 'Motivo de no retry (opcional)'}
        </DialogTitle>
        <DialogContent>
          <label htmlFor="status-reason-input" style={{ display: 'block', marginTop: 8, marginBottom: 6 }}>
            Escribe una nota breve para dejar contexto del cambio.
          </label>
          <input
            id="status-reason-input"
            type="text"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            placeholder="Ej. Cliente solicitó no volver a contactar"
            style={{ width: '100%', padding: '0.5rem 0.65rem' }}
            maxLength={250}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusReasonModal} disabled={statusReasonSubmitting} color="warning" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmStatusReasonModal} disabled={statusReasonSubmitting} color="primary" variant="contained">
            {statusReasonSubmitting ? 'Guardando...' : 'Aceptar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReactivateSubscriptionModal}
        onClose={closeReactivateSubscriptionModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ backgroundColor: '#e7f1ff' }}>
          Reactivar contacto para futuros envíos
        </DialogTitle>
        <DialogContent>
          <p style={{ marginTop: 8, marginBottom: 0 }}>
            Este contacto estaba marcado como no apto. ¿Quieres dejarlo en pendiente y reactivar también su suscripción global
            para correo y WhatsApp?
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReactivateSubscriptionModal} disabled={reactivateSubscriptionSubmitting} color="warning" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={confirmPendingWithoutReactivation} disabled={reactivateSubscriptionSubmitting} color="inherit" variant="outlined">
            Pendiente sin reactivar
          </Button>
          <Button onClick={confirmPendingWithReactivation} disabled={reactivateSubscriptionSubmitting} color="primary" variant="contained">
            {reactivateSubscriptionSubmitting ? 'Guardando...' : 'Pendiente + reactivar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer
        containerId="whatsapp-admin-toast"
        position="top-center"
        autoClose={4000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
        newestOnTop
      />
    </div>
  );
}

export default AdminWhatsappDashboard;
